import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {NULL_SENTINEL} from "~/components/ottopilotDataViewer/filterComponentStupider";
import {type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {KanbanBoardColumnConfig} from "~/components/ottopilotDataViewer/view";
import type {WorkspaceMobileNavigationConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import type {WorkspaceMobileBottomCreateItem} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import type {OttopilotListCardFunnelOptions} from "~/components/ottopilotDataViewer/ottopilotListCardPresentation";
import {type AonWorkspaceConfig, computeAonDaysFromCreatedAtIso} from "~/utilities/syntheticFields/aon";
import type {
    EventContainerDayPlanPageConfig as DayPlanPageConfig,
    EventContainerTableViewColumn as JourneyTableViewColumn,
    EventContainerScheduleCallDisableConfig as ScheduleCallDisableConfig,
} from "~/domainModels/workspaceModules/eventContainersModule";

import {isEventContainerBlockedByGlobalConstraints} from "~/domainModels/workspaceModules/eventContainerEditPanelConstraints";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {
    OttopilotAdditionalButtonKind,
    OttopilotAdditionalButtonFormFieldPresentation,
    OttopilotAdditionalButtonFormFieldRole,
    OttopilotOperator,
    OttopilotRunTimeValueKind,
    type OttopilotAdditionalButtonVisibilityRule,
    type OttopilotAdditionalButtonsConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import type {EventContainerWithEvents, EventWithEventContainer} from "~/utilities/apis/api";
import {getEventDispositionId} from "~/utilities/ottopilotSemanticColumns";
import {DateTime} from "luxon";
import {flatDispositionToDisplayNameFormat, formatDate, formatDateTime} from "~/utilities/utilities";
import {isIsoDateOnlyString} from "~/utilities/parseSafeDate";
import {
    OttopilotDispositionEmitWhen,
    OttopilotDispositionOptionRuleActionKind,
    OttopilotDispositionOptionRuleCountMode,
    OttopilotDispositionPluginKind,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import {
    OttopilotScheduleValueEncoding,
    OttopilotTrigger,
    OttopilotTriggerDataDriver,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import type {OttopilotDispositionConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import type {OttopilotTriggerConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions.ts";
import type {OttopilotDataViewerConfig} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDataViewerConfig";
import {uniqueUuid} from "@vesta/unique/unique";
import {
    type WorkspaceCapability,
    WorkspaceCapabilityStatus,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import {
    AMARON__CALL__EDIT__FORM__ID,
    AMARON__CALL__LOG__FORM__ID,
    AMARON__CALL__SCHEDULE__FORM__ID,
    AMARON__EVENT__CALLS__ACTION_PLATFORM__COLUMN_ID,
    AMARON__EVENT__CALLS__CALL_COUNT__COLUMN_ID,
    AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLATION_REASON__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT__CALLS__CREATED_BY_ROLE__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__FBCLID__COLUMN_ID,
    AMARON__EVENT__CALLS__FBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__GBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__GCLID__COLUMN_ID,
    AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    AMARON__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__REMARKS__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__AD_NAME__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__AD_SET_NAME__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CAR_MAKE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CAR_MODEL__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_HOUSE_NO__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_VOC__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBCLID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GCLID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__SERVICE_REQUESTED__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__SMARTPING_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__SERVICE__ASSIGNED_MARSHAL__COLUMN_ID,
    AMARON__EVENT__SERVICE__BOOKED_BY__COLUMN_ID,
    AMARON__EVENT__SERVICE__BOOKED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__BOOKING_STATUS__COLUMN_ID,
    AMARON__EVENT__SERVICE__CANCELLATION_REASON__COLUMN_ID,
    AMARON__EVENT__SERVICE__CANCELLED_BY__COLUMN_ID,
    AMARON__EVENT__SERVICE__CANCELLED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID,
    AMARON__EVENT__SERVICE__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT__SERVICE__COUPON_CODE__COLUMN_ID,
    AMARON__EVENT__SERVICE__CREATED_BY_ROLE__COLUMN_ID,
    AMARON__EVENT__SERVICE__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__SERVICE__CUSTOMER_HOUSE_NO__COLUMN_ID,
    AMARON__EVENT__SERVICE__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT__SERVICE__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT__SERVICE__DELIVERED_BY__COLUMN_ID,
    AMARON__EVENT__SERVICE__DELIVERED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__DISCOUNT_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__FRANCHISE__COLUMN_ID,
    AMARON__EVENT__SERVICE__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBCLID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__GBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GCLID__COLUMN_ID,
    AMARON__EVENT__SERVICE__MAKE_NAME__COLUMN_ID,
    AMARON__EVENT__SERVICE__MECHANIC_ARRIVAL_TIME__COLUMN_ID,
    AMARON__EVENT__SERVICE__MECHANIC_DISPATCH_TIME__COLUMN_ID,
    AMARON__EVENT__SERVICE__MODEL_NAME__COLUMN_ID,
    AMARON__EVENT__SERVICE__NOTES__COLUMN_ID,
    AMARON__EVENT__SERVICE__ORDER_FEEDBACK_DETAILS__COLUMN_ID,
    AMARON__EVENT__SERVICE__ORDER_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__ORDER_STAR_FEEDBACK__COLUMN_ID,
    AMARON__EVENT__SERVICE__PACKAGES__COLUMN_ID,
    AMARON__EVENT__SERVICE__PARENT_ORDER_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__PAYMENT_LINK_STATUS__COLUMN_ID,
    AMARON__EVENT__SERVICE__PAYMENT_LINK_URL__COLUMN_ID,
    AMARON__EVENT__SERVICE__RESCHEDULED_BY__COLUMN_ID,
    AMARON__EVENT__SERVICE__RESCHEDULED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_ADVISOR__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_CHARGE_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_COMPLETED_AT__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_STARTED_AT__COLUMN_ID,
    AMARON__EVENT__SERVICE__SUBTOTAL__COLUMN_ID,
    AMARON__EVENT__SERVICE__TAX_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__TIME_SLOT__COLUMN_ID,
    AMARON__EVENT__SERVICE__TOTAL_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_TERM__COLUMN_ID,
    AMARON__SERVICE__BOOK__FORM__ID,
    AMARON__SERVICE__EDIT__FORM__ID,
    AMARON__SERVICE__LOG__FORM__ID,
    AmaronCallStatusEnum,
    amaronCallStatuses,
    AmaronEnquiryStatusEnum,
    amaronEnquiryStatuses,
    AmaronEventTypes,
    AmaronServiceBookingStatusEnum,
    amaronServiceBookingStatuses,
    amaronServiceCategories,
    AmaronServiceCategoryEnum,
    getAmaronServiceBookingStatusSelectOptions,
    getAmaronServiceCategorySelectOptions,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/Events";
import {
    AMARON__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AD_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AD_SET_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MAKE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MODEL__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_HOUSE_NO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_VOC__COLUMN_ID,
    AMARON__EVENT_CONTAINER__DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ENQUIRIES_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_EVENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_EVENT_TIMESTAMP__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_CATEGORY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__PREFERRED_CALLING_TIME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__QUOTED_PRICE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SMARTPING_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_UPGRADE_REQUIRED__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__TERMS_PRIVACY_CONSENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_TERM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__WHATSAPP_CONSENT__COLUMN_ID,
    AmaronFuelTypeEnum,
    amaronFuelTypes,
    AmaronInqueueStatusEnum,
    amaronInqueueStatuses,
    AmaronEventContainerDispositionEnum,
    amaronEventContainerDispositions,
    AmaronLeadCategoryEnum,
    amaronLeadCategories,
    AmaronLeadStatusEnum,
    amaronLeadStatuses,
    AmaronPreferredCallingTimeEnum,
    amaronPreferredCallingTimes,
    AmaronServiceRequestedEnum,
    amaronServiceRequestedTypes,
    AmaronServiceUpgradeRequiredEnum,
    amaronServiceUpgradeRequiredOptions,
    getAmaronInqueueStatusFilterPickerValues,
    getAmaronLeadCategoryFilterPickerValues,
    formatAmaronLeadCategoryFilterValue,
    getAmaronEventContainerDispositions,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/EventContainers";
import type {
    AmaronDispositionLevelKey,
    AmaronDispositionLevels,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/EventContainers";

/** System / computed journey columns excluded from Activity Timeline "Detail Edited" cards. */
export const AMARON_JOURNEY_ACTIVITY_TIMELINE_SKIP_COLUMN_IDS: ReadonlyArray<Uuid> = [
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ENQUIRIES_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_EVENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_EVENT_TIMESTAMP__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_TERM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AD_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AD_SET_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__WHATSAPP_CONSENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__TERMS_PRIVACY_CONSENT__COLUMN_ID,
];

import {
    AMARON__OU__COUNTRY__NAME__COLUMN_ID,
    AMARON__OU__PINCODE__NAME__COLUMN_ID,
    AMARON__OU__STATE__NAME__COLUMN_ID,
    AmaronOus,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/OrganizationalUnits";
import {
    AMARON__USER_ADMIN__COUNTRIES__COLUMN_ID,
    AMARON__USER_AGENT__PINCODES__COLUMN_ID,
    AMARON__USER_MANAGER__STATES__COLUMN_ID,
    AmaronRoles,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/Users";
import {AmaronModuleNames} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/Workspace";
import {token} from "@atlaskit/tokens";
import type {OttopilotEventContainerConstraint} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerConstraint.ts";

export {
    amaronLeadStatuses,
    amaronInqueueStatuses,
    amaronEnquiryStatuses,
    amaronFuelTypes,
    amaronServiceRequestedTypes,
    amaronCallStatuses,
    amaronServiceCategories,
    amaronServiceBookingStatuses,
    amaronEventContainerDispositions,
    amaronPreferredCallingTimes,
    amaronLeadCategories,
    amaronServiceUpgradeRequiredOptions,
    getAmaronInqueueStatusFilterPickerValues,
    getAmaronLeadCategoryFilterPickerValues,
    formatAmaronLeadCategoryFilterValue,
    getAmaronServiceCategorySelectOptions,
    getAmaronServiceBookingStatusSelectOptions,
    getAmaronEventContainerDispositions,
};

export function getAmaronPreferredCallingTimeFilterPickerValues(): Array<string> {
    return Object.values(amaronPreferredCallingTimes).map((option) => String(option.id));
}

export function formatAmaronPreferredCallingTimeFilterValue(value: string): string {
    const trimmed = value.trim();
    if (trimmed === "") {
        return "";
    }
    const matched = Object.values(amaronPreferredCallingTimes).find((option) => String(option.id) === trimmed);
    return matched?.displayName ?? trimmed;
}

export function getAmaronServiceUpgradeRequiredFilterPickerValues(): Array<string> {
    return Object.values(amaronServiceUpgradeRequiredOptions).map((option) => String(option.id));
}

export function formatAmaronServiceUpgradeRequiredFilterValue(value: string): string {
    const trimmed = value.trim();
    if (trimmed === "") {
        return "";
    }
    const matched = Object.values(amaronServiceUpgradeRequiredOptions).find((option) => String(option.id) === trimmed);
    return matched?.displayName ?? trimmed;
}

export type {AmaronDispositionLevelKey, AmaronDispositionLevels};
export {
    AmaronOus,
    AmaronRoles,
    AmaronEventTypes,
    AmaronModuleNames,
    AMARON__OU__COUNTRY__NAME__COLUMN_ID,
    AMARON__OU__STATE__NAME__COLUMN_ID,
    AMARON__OU__PINCODE__NAME__COLUMN_ID,
    AMARON__USER_MANAGER__STATES__COLUMN_ID,
    AMARON__USER_AGENT__PINCODES__COLUMN_ID,
    AMARON__USER_ADMIN__COUNTRIES__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__PREFERRED_CALLING_TIME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_CATEGORY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__QUOTED_PRICE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_UPGRADE_REQUIRED__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_HOUSE_NO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_VOC__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AD_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AD_SET_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SMARTPING_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MAKE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MODEL__COLUMN_ID,
    AMARON__EVENT_CONTAINER__WHATSAPP_CONSENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__TERMS_PRIVACY_CONSENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ENQUIRIES_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_TERM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_EVENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_EVENT_TIMESTAMP__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_ID__COLUMN_ID,
    AmaronPreferredCallingTimeEnum,
    AmaronLeadCategoryEnum,
    AmaronServiceUpgradeRequiredEnum,
    AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_HOUSE_NO__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__AD_NAME__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__AD_SET_NAME__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GCLID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBCLID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_VOC__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__SERVICE_REQUESTED__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CAR_MAKE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CAR_MODEL__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__SMARTPING_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    AMARON__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__REMARKS__COLUMN_ID,
    AMARON__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLATION_REASON__COLUMN_ID,
    AMARON__EVENT__CALLS__CREATED_BY_ROLE__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT__CALLS__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT__CALLS__CALL_COUNT__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__CALLS__ACTION_PLATFORM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__GBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__GCLID__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__FBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__FBCLID__COLUMN_ID,
    AMARON__EVENT__SERVICE__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT__SERVICE__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT__SERVICE__BOOKING_STATUS__COLUMN_ID,
    AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID,
    AMARON__EVENT__SERVICE__ORDER_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__PARENT_ORDER_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__ASSIGNED_MARSHAL__COLUMN_ID,
    AMARON__EVENT__SERVICE__MAKE_NAME__COLUMN_ID,
    AMARON__EVENT__SERVICE__MODEL_NAME__COLUMN_ID,
    AMARON__EVENT__SERVICE__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT__SERVICE__PACKAGES__COLUMN_ID,
    AMARON__EVENT__SERVICE__SUBTOTAL__COLUMN_ID,
    AMARON__EVENT__SERVICE__DISCOUNT_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__TAX_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_CHARGE_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__TOTAL_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__COUPON_CODE__COLUMN_ID,
    AMARON__EVENT__SERVICE__PAYMENT_LINK_URL__COLUMN_ID,
    AMARON__EVENT__SERVICE__PAYMENT_LINK_STATUS__COLUMN_ID,
    AMARON__EVENT__SERVICE__TIME_SLOT__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_ADVISOR__COLUMN_ID,
    AMARON__EVENT__SERVICE__FRANCHISE__COLUMN_ID,
    AMARON__EVENT__SERVICE__NOTES__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_STARTED_AT__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_COMPLETED_AT__COLUMN_ID,
    AMARON__EVENT__SERVICE__MECHANIC_ARRIVAL_TIME__COLUMN_ID,
    AMARON__EVENT__SERVICE__MECHANIC_DISPATCH_TIME__COLUMN_ID,
    AMARON__EVENT__SERVICE__ORDER_STAR_FEEDBACK__COLUMN_ID,
    AMARON__EVENT__SERVICE__ORDER_FEEDBACK_DETAILS__COLUMN_ID,
    AMARON__EVENT__SERVICE__BOOKED_BY__COLUMN_ID,
    AMARON__EVENT__SERVICE__BOOKED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__DELIVERED_BY__COLUMN_ID,
    AMARON__EVENT__SERVICE__DELIVERED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__CANCELLED_BY__COLUMN_ID,
    AMARON__EVENT__SERVICE__CANCELLED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__CANCELLATION_REASON__COLUMN_ID,
    AMARON__EVENT__SERVICE__RESCHEDULED_BY__COLUMN_ID,
    AMARON__EVENT__SERVICE__RESCHEDULED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__CREATED_BY_ROLE__COLUMN_ID,
    AMARON__EVENT__SERVICE__CUSTOMER_HOUSE_NO__COLUMN_ID,
    AMARON__EVENT__SERVICE__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT__SERVICE__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GCLID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBCLID__COLUMN_ID,
    AmaronLeadStatusEnum,
    AmaronInqueueStatusEnum,
    AmaronEnquiryStatusEnum,
    AmaronFuelTypeEnum,
    AmaronServiceRequestedEnum,
    AmaronCallStatusEnum,
    AmaronServiceCategoryEnum,
    AmaronServiceBookingStatusEnum,
    AmaronEventContainerDispositionEnum,
    AMARON__CALL__SCHEDULE__FORM__ID,
    AMARON__CALL__LOG__FORM__ID,
    AMARON__CALL__EDIT__FORM__ID,
    AMARON__SERVICE__BOOK__FORM__ID,
    AMARON__SERVICE__LOG__FORM__ID,
    AMARON__SERVICE__EDIT__FORM__ID,
};

export function isAmaronWorkspace(workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>): boolean {
    return workspaceCapabilities.some(
        (capability) => capability.type === WorkspaceCapabilityType.amaronSpecificBehaviour && capability.status === WorkspaceCapabilityStatus.enabled,
    );
}

/** System Admin, Admin, and Manager. Agents use My Day Plan instead of In Queue. */
export const AMARON_IN_QUEUE_NAV_VISIBLE_ROLE_TEMPLATE_IDS: ReadonlySet<string> = new Set<string>([
    AmaronRoles.SystemAdmin,
    AmaronRoles.Admin,
    AmaronRoles.Manager,
]);

/** Agent only. Managers/admins use In Queue and Leads, not My Day Plan. */
export const AMARON_DAY_PLAN_NAV_VISIBLE_ROLE_TEMPLATE_IDS: ReadonlySet<string> = new Set<string>([AmaronRoles.Agent]);

/** Blocks journey and event edit panels when lead status is Converted or Churned. */
export const AMARON_JOURNEY_NON_EDITABLE_LEAD_STATUS_GLOBAL_CONSTRAINTS: Array<OttopilotEventContainerConstraint> = [
    {
        type: "Status",
        columnType: "OttopilotEventContainer",
        refColumnId: AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
        isAdditionalColumn: true,
        disallowedStatuses: [amaronLeadStatuses[AmaronLeadStatusEnum.CONVERTED].id, amaronLeadStatuses[AmaronLeadStatusEnum.CHURNED].id],
    },
];

export const AMARON_SCHEDULE_CALL_ALREADY_SCHEDULED_MESSAGE = "A call is already scheduled for this lead.";

export const AMARON_JOURNEY_SCHEDULE_CALL_DISABLE_CONFIG: ScheduleCallDisableConfig = {
    disabledMessage: AMARON_SCHEDULE_CALL_ALREADY_SCHEDULED_MESSAGE,
    rules: [
        {
            type: "Present",
            columnType: "OttopilotEventContainer",
            refColumnId: AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID,
            isAdditionalColumn: true,
        },
    ],
};

export function isAmaronJourneyLeadStatusBlockingEdit(journey: EventContainerWithEvents): boolean {
    return isEventContainerBlockedByGlobalConstraints(journey, AMARON_JOURNEY_NON_EDITABLE_LEAD_STATUS_GLOBAL_CONSTRAINTS);
}

const AMARON_EVENT_CREATE_FORM_URL_PREFIXES = ["amaron/call/"] as const;

export function getAmaronListEventCreateItems(
    workspaceMobileNavigationConfiguration: WorkspaceMobileNavigationConfiguration | null,
): readonly WorkspaceMobileBottomCreateItem[] {
    const items = workspaceMobileNavigationConfiguration?.mobileBottomCreateSheet ?? [];
    return items.filter((item) => AMARON_EVENT_CREATE_FORM_URL_PREFIXES.some((prefix) => item.formUrl.startsWith(prefix)));
}

export type AmaronListModule = "journeysLeads" | "journeysInQueue" | "events";

export type AmaronListModuleConfig = {
    enabled: boolean;
    showFooter: boolean;
    scheduleRoute: string;
    includeEventCreateDrawer: boolean;
    mobileReplaceCreateWithLogCall?: boolean;
};

export const AMARON_LIST_MODULE_CONFIG: Readonly<Record<AmaronListModule, AmaronListModuleConfig>> = {
    journeysLeads: {
        enabled: true,
        showFooter: true,
        scheduleRoute: "",
        includeEventCreateDrawer: true,
        mobileReplaceCreateWithLogCall: true,
    },
    journeysInQueue: {
        enabled: true,
        showFooter: false,
        scheduleRoute: "",
        includeEventCreateDrawer: false,
    },
    events: {
        enabled: true,
        showFooter: true,
        scheduleRoute: "",
        includeEventCreateDrawer: true,
        mobileReplaceCreateWithLogCall: true,
    },
};

export function getAmaronListModuleConfig(module: AmaronListModule): AmaronListModuleConfig {
    return AMARON_LIST_MODULE_CONFIG[module];
}

/**
 * Events/sidebar title for the Calls template when workspace config uses typo "callses"
 * (preferred display: "Calles").
 */
export function normalizeAmaronCallsPluralDisplayLabel(pluralSentenceCase: string): string {
    const lower = pluralSentenceCase.trim().toLowerCase();
    if (lower === "callses" || lower === "calles") {
        return "Calls";
    }
    return pluralSentenceCase;
}

// Organizational Unit Template IDs
// Amaron OU hierarchy: Workspace -> Country -> State -> Pincode
// User Template IDs
// Amaron roles from architecture-amaron.csv
// Event Template IDs
// Amaron events from modules-amaron.csv
/** Ordered funnel stages for list-card circular funnel: enquiries → call → service. */
export type AmaronJourneyListFunnelStageKey = "enquiries" | "call" | "service";

export type AmaronJourneyListFunnelStage = {
    key: AmaronJourneyListFunnelStageKey;
    columnId: Uuid;
};

// Module IDs
// Organizational Unit Column IDs
/** Display name on Pincode OU rows. External-source OU resolution matches the postal code on schema property {@code pincode}, not this column. */

// User Column IDs - System Admin

// User Column IDs - Admin

// User Column IDs - Manager

// User Column IDs - Agent

// Invite / onboarding columns — parallel Dhiraagu user templates (see {@link DEFAULT_USER_TEMPLATE_COLUMN_MAP})

// Journey Column IDs (Lead in Amaron parlance)
/** Synthetic journey table columns — L1–L4 derived from the configured disposition UDO value. */

export const AMARON_JOURNEY_DISPOSITION_LEVEL_COLUMN_IDS = [
    AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID,
] as const;

export const AMARON_JOURNEY_LIST_FUNNEL_STAGES: readonly AmaronJourneyListFunnelStage[] = [
    {key: "enquiries", columnId: AMARON__EVENT_CONTAINER__ENQUIRIES_COUNT__COLUMN_ID},
    {key: "call", columnId: AMARON__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID},
    {key: "service", columnId: AMARON__EVENT_CONTAINER__SERVICE_COUNT__COLUMN_ID},
] as const;

export const AMARON_JOURNEY_LIST_FUNNEL_STAGE_COLUMN_IDS: readonly Uuid[] = AMARON_JOURNEY_LIST_FUNNEL_STAGES.map((stage) => stage.columnId);

export function createAmaronJourneyListFunnelOptions<T>(getAdditionalColumnValues: (row: T) => Record<Uuid, unknown>): OttopilotListCardFunnelOptions<T> {
    return {
        stageColumnIds: AMARON_JOURNEY_LIST_FUNNEL_STAGE_COLUMN_IDS,
        getAdditionalColumnValues,
    };
}

/**
 * Normalize journey Calls Count from API/storage (may be number or numeric string). Avoids string concatenation (e.g. "1" + 1 → "11").
 */
export function parseAmaronJourneyCallsCount(raw: unknown): number {
    if (raw === null || raw === undefined) {
        return 0;
    }
    if (typeof raw === "number") {
        if (Number.isFinite(raw) !== true) {
            return 0;
        }
        return Math.trunc(raw);
    }
    if (typeof raw === "boolean") {
        return raw === true ? 1 : 0;
    }
    if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (trimmed === "") {
            return 0;
        }
        const parsed = Number.parseInt(trimmed, 10);
        if (Number.isFinite(parsed) !== true) {
            return 0;
        }
        return parsed;
    }
    return 0;
}

/** AON (age) in whole days from journey `createdAt` to now; matches Emotorad-style synthetic column semantics. */
export function computeAmaronJourneyAonDaysFromCreatedAt(createdAtIso: string): string {
    const aonDays = computeAonDaysFromCreatedAtIso(createdAtIso);
    if (aonDays === null) {
        return "0";
    }
    return String(aonDays);
}

export const amaronJourneyAonConfig: AonWorkspaceConfig = {
    displayColumnId: String(AMARON__EVENT_CONTAINER__AON__COLUMN_ID),
    rangesColumnId: "__amaron_aon_ranges__",
    createdAtPickerColumnId: "createdAt",
    createdAtApiColumn: "createdAt",
};

/**
 * Amaron `/inqueue` table — explicit columns and order (system audit fields use journey row metadata keys).
 */
/** Leads (`/journeys`) — explicit columns for table and mobile list cards. */
export const AMARON_JOURNEY_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_HOUSE_NO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_VOC__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MAKE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MODEL__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SMARTPING_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AD_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__AD_SET_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__WHATSAPP_CONSENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__TERMS_PRIVACY_CONSENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ENQUIRIES_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_TERM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBCLID__COLUMN_ID,
    "createdBy",
    "createdAt",
];

export const AMARON_INQUEUE_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SMARTPING_CAMPAIGN__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_TERM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBCLID__COLUMN_ID,
    "createdAt",
    AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
];

//synthetic Colunms for event table

// Event Column IDs - Enquiry

// Event Column IDs - Calls

/** Legacy Schedule Date column; prefer this over Scheduled On when present on older rows. */
export function resolveAmaronCallScheduledOnDisplayValue(additionalColumnValues: Record<Uuid, unknown>): unknown {
    const legacyScheduleDate = additionalColumnValues[AMARON__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID];
    if (legacyScheduleDate !== null && legacyScheduleDate !== undefined && (typeof legacyScheduleDate !== "string" || legacyScheduleDate.trim() !== "")) {
        return legacyScheduleDate;
    }
    return additionalColumnValues[AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID];
}

// Event Column IDs - Service

/** Calls events list — explicit column order. */
export const AMARON_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__REMARKS__COLUMN_ID,
    AMARON__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLATION_REASON__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__CALLS__ACTION_PLATFORM__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    AMARON__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__GBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__GCLID__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__FBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__FBCLID__COLUMN_ID,
];

/** Enquiry events list — explicit column order. */
export const AMARON_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_HOUSE_NO__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__CUSTOMER_VOC__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MAKE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MODEL__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__SERVICE_REQUESTED__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__SMARTPING_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__AD_NAME__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__AD_SET_NAME__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    AMARON__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GCLID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBCLID__COLUMN_ID,
    "createdAt",
];

/** Service events list — explicit column order (matches workspace tableViewColumns). */
export const AMARON_SERVICE_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT__SERVICE__BOOKING_STATUS__COLUMN_ID,
    AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID,
    AMARON__EVENT__SERVICE__PARENT_ORDER_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__ORDER_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__ASSIGNED_MARSHAL__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MAKE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MODEL__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
    AMARON__EVENT__SERVICE__PACKAGES__COLUMN_ID,
    AMARON__EVENT__SERVICE__SUBTOTAL__COLUMN_ID,
    AMARON__EVENT__SERVICE__DISCOUNT_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__TAX_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_CHARGE_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__TOTAL_AMOUNT__COLUMN_ID,
    AMARON__EVENT__SERVICE__COUPON_CODE__COLUMN_ID,
    AMARON__EVENT__SERVICE__PAYMENT_LINK_URL__COLUMN_ID,
    AMARON__EVENT__SERVICE__PAYMENT_LINK_STATUS__COLUMN_ID,
    AMARON__EVENT__SERVICE__TIME_SLOT__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_ADVISOR__COLUMN_ID,
    AMARON__EVENT__SERVICE__FRANCHISE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_HOUSE_NO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__SERVICE__NOTES__COLUMN_ID,
    "createdBy",
    "createdAt",
    AMARON__EVENT__SERVICE__BOOKED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_STARTED_AT__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_COMPLETED_AT__COLUMN_ID,
    AMARON__EVENT__SERVICE__MECHANIC_ARRIVAL_TIME__COLUMN_ID,
    AMARON__EVENT__SERVICE__MECHANIC_DISPATCH_TIME__COLUMN_ID,
    AMARON__EVENT__SERVICE__ORDER_STAR_FEEDBACK__COLUMN_ID,
    AMARON__EVENT__SERVICE__ORDER_FEEDBACK_DETAILS__COLUMN_ID,
    AMARON__EVENT__SERVICE__CANCELLED_BY__COLUMN_ID,
    AMARON__EVENT__SERVICE__CANCELLED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__CANCELLATION_REASON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GCLID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBCLID__COLUMN_ID,
];

// Lead status enum (Live, Churned, Converted, Archived) - archive uses Archived value on journey status column
/**
 * Active / inactive lead-status UUID groups for the events table journey scope filter (Display by journey).
 * Returns null when the workspace is not Amaron — the control is not rendered.
 */
export function getAmaronJourneyScopeFilterStatusIdsForEvents(workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>): {
    activeStatusIds: Array<Uuid>;
    inactiveStatusIds: Array<Uuid>;
} | null {
    if (isAmaronWorkspace(workspaceCapabilities) !== true) {
        return null;
    }
    return {
        activeStatusIds: [AmaronLeadStatusEnum.LIVE as Uuid],
        inactiveStatusIds: [AmaronLeadStatusEnum.CHURNED as Uuid, AmaronLeadStatusEnum.CONVERTED as Uuid, AmaronLeadStatusEnum.ARCHIVED as Uuid],
    };
}

const AMARON__EVENTS__SCOPE_FILTER__ASSIGNED_TO_ME__OPTION_ID = uniqueUuid`e1f2a3b4-c5d6-4e7f-8a9b-0c1d2e3f4a5b`;
const AMARON__EVENTS__SCOPE_FILTER__CREATED_BY_ME__OPTION_ID = uniqueUuid`f2a3b4c5-d6e7-4f8a-9b0c-1d2e3f4a5b6c`;

/** Events list/calendar scope filter: Assigned to me / Created by me (lead owner column + event createdBy). */
export function getAmaronEventsPageScopeFilterConfig(leadOwnerColumnId: Uuid): OttopilotDataViewerConfig["scopeFilter"] {
    return {
        drawerTitle: "Type",
        selectionMode: "multi",
        options: [
            {
                id: AMARON__EVENTS__SCOPE_FILTER__ASSIGNED_TO_ME__OPTION_ID,
                filterKey: String(leadOwnerColumnId),
                label: "Assigned to me",
                type: "User",
                filterType: "equals",
            },
            {
                id: AMARON__EVENTS__SCOPE_FILTER__CREATED_BY_ME__OPTION_ID,
                filterKey: "createdBy",
                label: "Created by me",
                type: "User",
                filterType: "equals",
            },
        ],
    };
}

/** Restricts AccountSelector journey search to Live leads in Amaron workspaces. */
export function getAmaronAccountSelectorEventContainerFilterLineItems(
    workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>,
): Array<FilterConfigurationLineItem> {
    if (isAmaronWorkspace(workspaceCapabilities) !== true) {
        return [];
    }
    return [
        {
            columnId: AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
            operation: FilterOperation.equals,
            value: amaronLeadStatuses[AmaronLeadStatusEnum.LIVE].id,
        },
    ];
}

export function formatAmaronInqueueStatusFilterValue(value: string): string {
    const trimmed = value.trim();
    if (trimmed === "") {
        return "";
    }
    const matched = Object.values(amaronInqueueStatuses).find((status) => String(status.id) === trimmed);
    return matched?.displayName ?? trimmed;
}

function resolveLatestAmaronJourneyActivityInstant(journey: EventContainerWithEvents): DateTime | null {
    let latest: DateTime | null = null;
    const consider = (iso: string): void => {
        const dt = DateTime.fromISO(iso);
        if (dt.isValid !== true) {
            return;
        }
        if (latest === null || dt > latest) {
            latest = dt;
        }
    };

    consider(journey.updatedAt);
    consider(journey.createdAt);
    for (const event of journey.events) {
        consider(event.updatedAt);
        consider(event.createdAt);
    }

    return latest;
}

function amaronScalarText(rawValue: unknown): string | null {
    if (typeof rawValue === "string") {
        const trimmed = rawValue.trim();
        return trimmed === "" ? null : trimmed;
    }
    if (typeof rawValue === "number" || typeof rawValue === "boolean" || typeof rawValue === "bigint") {
        return String(rawValue);
    }
    return null;
}

function parseAmaronStoredTimestamp(rawValue: unknown): DateTime | null {
    const trimmed = amaronScalarText(rawValue);
    if (trimmed === null || trimmed === "-") {
        return null;
    }
    const isoCandidate = trimmed.includes("T") === true ? trimmed : trimmed.replace(" ", "T");
    const fromIso = DateTime.fromISO(isoCandidate, {zone: "Asia/Kolkata"});
    if (fromIso.isValid === true) {
        return fromIso;
    }
    const fromSql = DateTime.fromSQL(trimmed, {zone: "Asia/Kolkata"});
    if (fromSql.isValid === true) {
        return fromSql;
    }
    return null;
}

function formatAmaronStoredTimestampDisplay(rawValue: unknown): string | null {
    const parsed = parseAmaronStoredTimestamp(rawValue);
    if (parsed === null) {
        return null;
    }
    return formatDateTime(parsed.toISO() ?? "");
}

/** Formats scheduled call date from PostgreSQL timestamp or ISO date-only strings. */
export function formatAmaronScheduledCallDateDisplay(rawValue: unknown): string {
    const trimmed = amaronScalarText(rawValue);
    if (trimmed === null || trimmed === "-") {
        return "-";
    }
    if (isIsoDateOnlyString(trimmed) === true) {
        return formatDate(trimmed);
    }
    const parsed = parseAmaronStoredTimestamp(rawValue);
    if (parsed !== null) {
        return parsed.setZone("Asia/Kolkata").toFormat("d LLL yyyy");
    }
    return "-";
}

/** Formats Last Activity On when the stored value is missing or not a valid timestamp. */
export function resolveAmaronLastActivityOnDisplay(journey: EventContainerWithEvents, rawValue: unknown): string {
    const formatted = formatAmaronStoredTimestampDisplay(rawValue);
    if (formatted !== null) {
        return formatted;
    }

    const latest = resolveLatestAmaronJourneyActivityInstant(journey);
    if (latest === null) {
        return "-";
    }

    return formatDateTime(latest.toISO() ?? "");
}

/** Maps stored last-activity event template UUIDs to display labels (Leads, Disposition Tracker, …). */
export function formatAmaronJourneyLastActivityDisplay(value: string, eventTemplates: ReadonlyArray<{id: Uuid; displayName: string}>): string {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === "-") {
        return "-";
    }
    const fromTemplate = eventTemplates.find((template) => String(template.id) === trimmed);
    if (fromTemplate !== undefined) {
        return fromTemplate.displayName;
    }
    for (const [eventTypeName, eventTypeId] of Object.entries(AmaronEventTypes)) {
        if (String(eventTypeId) === trimmed) {
            return eventTypeName === "Calls" ? "Call" : eventTypeName;
        }
    }
    return trimmed;
}

/** True when journey Inqueue Status is Assigned (UUID or display name). */
export function isAmaronJourneyInqueueStatusAssigned(additionalColumnValues: Record<string, unknown>): boolean {
    const raw = additionalColumnValues[AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID];
    if (raw === null || raw === undefined) {
        return false;
    }
    if (typeof raw !== "string") {
        return false;
    }
    const normalized = raw.trim().toLowerCase();
    if (normalized === "") {
        return false;
    }
    if (normalized === String(AmaronInqueueStatusEnum.ASSIGNED).toLowerCase()) {
        return true;
    }
    return normalized === amaronInqueueStatuses[AmaronInqueueStatusEnum.ASSIGNED].displayName.trim().toLowerCase();
}

// Enquiry status enum (Open, Engaged, Non-Engaged, Archive) - archive stores row as Archive
// Fuel types (vehicle) — Journey + Enquiry event select options
// Fuel types (vehicle) — Journey + Enquiry event select options
export function formatAmaronCallStatusIdForDisplay(statusId: string): string {
    const normalized = statusId.trim().toLowerCase();
    if (normalized === "") {
        return "";
    }
    for (const status of Object.values(amaronCallStatuses)) {
        if (String(status.id).toLowerCase() === normalized) {
            return status.displayName;
        }
    }
    return statusId;
}

export function formatAmaronFuelTypeIdForDisplay(fuelTypeId: string): string {
    const normalized = fuelTypeId.trim().toLowerCase();
    if (normalized === "") {
        return "";
    }
    for (const fuelType of Object.values(amaronFuelTypes)) {
        if (String(fuelType.id).toLowerCase() === normalized) {
            return fuelType.displayName;
        }
    }
    return fuelTypeId;
}

const AMARON_OPTION_LOOKUP_TABLES: Array<Record<string, {id: Uuid; displayName: string}>> = [
    amaronLeadStatuses,
    amaronInqueueStatuses,
    amaronEnquiryStatuses,
    amaronFuelTypes,
    amaronServiceRequestedTypes,
    amaronCallStatuses,
    amaronServiceCategories,
    amaronServiceBookingStatuses,
    amaronEventContainerDispositions,
];

export function formatAmaronOptionIdForDisplay(optionId: string): string {
    const normalized = optionId.trim().toLowerCase();
    if (normalized === "") {
        return "";
    }
    for (const table of AMARON_OPTION_LOOKUP_TABLES) {
        for (const entry of Object.values(table)) {
            if (String(entry.id).toLowerCase() === normalized) {
                return entry.displayName;
            }
        }
    }
    return optionId;
}

// Service Category drives module status. Service Booking Status comes from vos-types.

/** Archive is hidden from Amaron event kanban — rows remain reachable via table/filters. */
function filterAmaronKanbanColumnConfigExcludingArchive(
    columnConfig: ReadonlyArray<KanbanBoardColumnConfig>,
    archiveStatusId: string,
): Array<KanbanBoardColumnConfig> {
    const archiveStatusIdString = String(archiveStatusId);
    return columnConfig.filter(
        (column) => String(column.id) !== archiveStatusIdString && column.possibleValues.every((value) => String(value) !== archiveStatusIdString),
    );
}

export function filterAmaronEnquiryKanbanColumnConfig(columnConfig: ReadonlyArray<KanbanBoardColumnConfig>): Array<KanbanBoardColumnConfig> {
    return filterAmaronKanbanColumnConfigExcludingArchive(columnConfig, AmaronEnquiryStatusEnum.ARCHIVE);
}

export function filterAmaronCallKanbanColumnConfig(columnConfig: ReadonlyArray<KanbanBoardColumnConfig>): Array<KanbanBoardColumnConfig> {
    return filterAmaronKanbanColumnConfigExcludingArchive(columnConfig, AmaronCallStatusEnum.ARCHIVE);
}

export function filterAmaronServiceKanbanColumnConfig(columnConfig: ReadonlyArray<KanbanBoardColumnConfig>): Array<KanbanBoardColumnConfig> {
    return filterAmaronKanbanColumnConfigExcludingArchive(columnConfig, AmaronServiceCategoryEnum.ARCHIVE);
}

type AmaronEventKanbanBoardCardView = OttopilotEventTemplate["configuration"]["eventPage"]["kanbanBoardCardView"];
type AmaronEventKanbanCardDetailConfig = AmaronEventKanbanBoardCardView["details"][number][number];

/** UTM platform/source are rendered in the Amaron enquiry kanban secondary section instead. */
const AMARON_ENQUIRY_KANBAN_SECONDARY_DETAIL_COLUMN_IDS: ReadonlySet<string> = new Set([
    String(AMARON__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID),
    String(AMARON__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID),
    String(AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID),
    String(AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID),
]);

const AMARON_ENQUIRY_KANBAN_SECONDARY_DETAIL_LABELS: ReadonlySet<string> = new Set(["utm platform", "utm source", "utm_platform", "utm_source"]);

function amaronEnquiryKanbanDetailConfigIsExcluded(columnDetail: AmaronEventKanbanCardDetailConfig): boolean {
    const normalizedLabel = columnDetail.label.trim().toLowerCase().replace(/_/g, " ");
    if (AMARON_ENQUIRY_KANBAN_SECONDARY_DETAIL_LABELS.has(normalizedLabel) === true) {
        return true;
    }
    if (AMARON_ENQUIRY_KANBAN_SECONDARY_DETAIL_LABELS.has(columnDetail.label.trim().toLowerCase()) === true) {
        return true;
    }
    const dataSources = columnDetail.type === "SingleColumn" ? [columnDetail.dataSource] : columnDetail.dataSource;
    return dataSources.some((dataSource) => AMARON_ENQUIRY_KANBAN_SECONDARY_DETAIL_COLUMN_IDS.has(String(dataSource)));
}

function filterAmaronEnquiryKanbanBoardCardView(kanbanBoardCardView: AmaronEventKanbanBoardCardView): AmaronEventKanbanBoardCardView {
    return {
        ...kanbanBoardCardView,
        heading: kanbanBoardCardView.heading.filter((id) => AMARON_ENQUIRY_KANBAN_SECONDARY_DETAIL_COLUMN_IDS.has(String(id)) === false),
        details: kanbanBoardCardView.details
            .map((row) => row.filter((col) => amaronEnquiryKanbanDetailConfigIsExcluded(col) === false))
            .filter((row) => row.length > 0),
    };
}

/** Amaron enquiry kanban - hide template UTM fields shown in the secondary details section. */
export function resolveAmaronEventKanbanBoardCardView(
    eventTemplateId: Uuid,
    kanbanBoardCardView: AmaronEventKanbanBoardCardView,
): AmaronEventKanbanBoardCardView {
    if (eventTemplateId === (AmaronEventTypes.Enquiry as Uuid)) {
        return filterAmaronEnquiryKanbanBoardCardView(kanbanBoardCardView);
    }
    return kanbanBoardCardView;
}

function normalizeAmaronDashboardDispositionLookupKey(value: string): string {
    return value.trim().toLowerCase();
}

function looksLikeUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

/** Maps analytics disposition dimension values (id or flat slug) to a display label for Amaron dashboard widgets. */
export function resolveAmaronDashboardDispositionDisplayLabel(
    raw: unknown,
    workspaceDispositions: ReadonlyArray<{id: Uuid; displayName: string}> = [],
): string {
    if (typeof raw !== "string") {
        return "Unknown";
    }
    const value = raw.trim();
    if (value === "") {
        return "Unknown";
    }
    const normalizedValue = normalizeAmaronDashboardDispositionLookupKey(value);

    for (const disposition of Object.values(amaronEventContainerDispositions)) {
        if (normalizeAmaronDashboardDispositionLookupKey(String(disposition.id)) === normalizedValue) {
            return disposition.displayName;
        }
    }

    for (const disposition of workspaceDispositions) {
        if (normalizeAmaronDashboardDispositionLookupKey(String(disposition.id)) === normalizedValue) {
            const label = disposition.displayName.trim();
            return label === "" ? "Others" : label;
        }
    }

    for (const disposition of Object.values(amaronEventContainerDispositions)) {
        if (normalizeAmaronDashboardDispositionLookupKey(disposition.flatDisposition) === normalizedValue) {
            return disposition.displayName;
        }
    }

    if (value.includes("_") || (value.includes("-") && looksLikeUuid(value) !== true)) {
        return flatDispositionToDisplayNameFormat(value);
    }

    if (looksLikeUuid(value) === true) {
        return "Others";
    }

    return value;
}

/**
 * Splits an Amaron flat disposition slug into L1–L4.
 * Each underscore-separated segment maps to one level; segments after L3 roll into L4.
 */
export function splitAmaronFlatDispositionIntoLevels(flatDisposition: string): AmaronDispositionLevels {
    const parts = flatDisposition.split("_");
    return {
        l1: parts[0] ?? "",
        l2: parts[1] ?? "",
        l3: parts[2] ?? "",
        l4: parts.length <= 3 ? "" : parts.slice(3).join("_"),
    };
}

export function amaronFlatDispositionForDispositionId(dispositionId: Uuid): string | undefined {
    for (const disposition of Object.values(amaronEventContainerDispositions)) {
        if (disposition.id === dispositionId) {
            return disposition.flatDisposition;
        }
    }
    return undefined;
}

function parseAmaronDispositionIdRaw(raw: unknown): Uuid | null {
    if (typeof raw !== "string" || raw.trim() === "") {
        return null;
    }
    return raw.trim() as Uuid;
}

type AmaronJourneyDispositionSource = {
    createdAt?: string;
    updatedAt?: string;
    events: Array<{
        id?: Uuid | string;
        templateId: Uuid;
        createdAt: string;
        additionalColumnValues: Record<string, unknown>;
    }>;
    additionalColumnValues: Record<string, unknown>;
};

function resolveLatestAmaronCallDispositionOnJourney(journey: AmaronJourneyDispositionSource): {dispositionId: Uuid; loggedAtMs: number} | null {
    let latestDispositionId: Uuid | null = null;
    let latestLoggedAtMs = Number.NEGATIVE_INFINITY;

    for (const event of journey.events) {
        if (event.templateId !== (AmaronEventTypes.Calls as Uuid)) {
            continue;
        }
        const dispositionId = parseAmaronDispositionIdRaw(event.additionalColumnValues[AMARON__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID]);
        if (dispositionId === null) {
            continue;
        }
        const loggedOnRaw = event.additionalColumnValues[AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID];
        const loggedAtMs =
            typeof loggedOnRaw === "string" && loggedOnRaw.trim() !== "" ? new Date(loggedOnRaw.trim()).getTime() : new Date(event.createdAt).getTime();
        if (Number.isFinite(loggedAtMs) && loggedAtMs >= latestLoggedAtMs) {
            latestLoggedAtMs = loggedAtMs;
            latestDispositionId = dispositionId;
        }
    }

    if (latestDispositionId === null) {
        return null;
    }
    return {dispositionId: latestDispositionId, loggedAtMs: latestLoggedAtMs};
}

/** Digits-only contact key for deduping duplicate leads on the disposition kanban. */
export function normalizeAmaronJourneyContactNumber(raw: unknown): string {
    if (typeof raw !== "string") {
        return "";
    }
    return raw.replace(/\D/g, "");
}

/** Milliseconds of the latest call disposition log, else journey `createdAt`. */
export function resolveAmaronJourneyLatestDispositionActivityMs(journey: AmaronJourneyDispositionSource): number {
    const latestCall = resolveLatestAmaronCallDispositionOnJourney(journey);
    if (latestCall !== null) {
        return latestCall.loggedAtMs;
    }
    // const updatedMs = journey.updatedAt !== undefined ? new Date(journey.updatedAt).getTime() : Number.NaN;
    // if (Number.isFinite(updatedMs)) {
    //     return updatedMs;
    // }
    const createdMs = journey.createdAt !== undefined ? new Date(journey.createdAt).getTime() : Number.NaN;
    return Number.isFinite(createdMs) ? createdMs : 0;
}

/**
 * Flat disposition path for kanban column placement.
 * Uses the current journey disposition, then its L1-L4 columns.
 * Historical calls do not override the lead's current state.
 */
export function resolveAmaronJourneyFlatDispositionForKanban(
    journey: AmaronJourneyDispositionSource,
    journeyDispositionId: Uuid | null,
): string {
    const fromJourneyDisposition = journeyDispositionId === null
        ? undefined
        : amaronFlatDispositionForDispositionId(journeyDispositionId);
    if (fromJourneyDisposition !== undefined) {
        return fromJourneyDisposition;
    }

    return [
        journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID],
        journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID],
        journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID],
        journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID],
    ]
        .filter((part) => typeof part === "string" && part.trim() !== "")
        .join("_");
}

function amaronDispositionParentLevelValueMatchesFilter(filterValues: Array<string>, levelValue: string): boolean {
    return filterValues.some((filterValue) => {
        if (filterValue === NULL_SENTINEL) {
            return levelValue === "";
        }
        return filterValue === levelValue;
    });
}

function amaronDispositionLevelValueMatchesFilter(filterValues: Array<string>, levelValue: string): boolean {
    return amaronDispositionParentLevelValueMatchesFilter(filterValues, levelValue);
}

export function getAmaronUniqueDispositionLevelValues(
    level: AmaronDispositionLevelKey,
    parentLevels?: Partial<Record<AmaronDispositionLevelKey, Array<string>>>,
): Array<string> {
    const values = new Set<string>();
    let hasEmptyLevelValue = false;
    for (const disposition of Object.values(amaronEventContainerDispositions)) {
        const levels = splitAmaronFlatDispositionIntoLevels(disposition.flatDisposition);
        if (parentLevels !== undefined) {
            const parentsMatch = (Object.entries(parentLevels) as Array<[AmaronDispositionLevelKey, Array<string> | undefined]>).every(
                ([parentLevel, parentValues]) => {
                    if (parentValues === undefined || parentValues.length === 0) {
                        return true;
                    }
                    return amaronDispositionParentLevelValueMatchesFilter(parentValues, levels[parentLevel]);
                },
            );
            if (parentsMatch !== true) {
                continue;
            }
        }
        const value = levels[level];
        if (value === "") {
            hasEmptyLevelValue = true;
            continue;
        }
        values.add(value);
    }
    const sortedValues = Array.from(values).sort((left, right) => left.localeCompare(right));
    if (hasEmptyLevelValue === true && (level === "l2" || level === "l3" || level === "l4")) {
        return [NULL_SENTINEL, ...sortedValues];
    }
    return sortedValues;
}

export function getAmaronDispositionIdsForLevels(args: {
    l1Values?: Array<string>;
    l2Values?: Array<string>;
    l3Values?: Array<string>;
    l4Values?: Array<string>;
}): Array<Uuid> {
    const result: Array<Uuid> = [];
    for (const disposition of Object.values(amaronEventContainerDispositions)) {
        const levels = splitAmaronFlatDispositionIntoLevels(disposition.flatDisposition);
        const matchesL1 = args.l1Values === undefined || amaronDispositionLevelValueMatchesFilter(args.l1Values, levels.l1);
        const matchesL2 = args.l2Values === undefined || amaronDispositionLevelValueMatchesFilter(args.l2Values, levels.l2);
        const matchesL3 = args.l3Values === undefined || amaronDispositionLevelValueMatchesFilter(args.l3Values, levels.l3);
        const matchesL4 = args.l4Values === undefined || amaronDispositionLevelValueMatchesFilter(args.l4Values, levels.l4);
        if (matchesL1 && matchesL2 && matchesL3 && matchesL4) {
            result.push(disposition.id);
        }
    }
    return result;
}

export function getAmaronParentDispositionLevelsForFilterLevel(
    level: AmaronDispositionLevelKey,
    parentLevels: Partial<Record<AmaronDispositionLevelKey, Array<string>>>,
): Partial<Record<AmaronDispositionLevelKey, Array<string>>> {
    const result: Partial<Record<AmaronDispositionLevelKey, Array<string>>> = {};
    if (level === "l2" || level === "l3" || level === "l4") {
        const l1Values = parentLevels.l1;
        if (l1Values !== undefined && l1Values.length > 0) {
            result.l1 = l1Values;
        }
    }
    if (level === "l3" || level === "l4") {
        const l2Values = parentLevels.l2;
        if (l2Values !== undefined && l2Values.length > 0) {
            result.l2 = l2Values;
        }
    }
    if (level === "l4") {
        const l3Values = parentLevels.l3;
        if (l3Values !== undefined && l3Values.length > 0) {
            result.l3 = l3Values;
        }
    }
    return result;
}

export function getAmaronChildDispositionLevelKeys(level: AmaronDispositionLevelKey): Array<AmaronDispositionLevelKey> {
    switch (level) {
        case "l1":
            return ["l2", "l3", "l4"];
        case "l2":
            return ["l3", "l4"];
        case "l3":
            return ["l4"];
        case "l4":
            return [];
    }
}

export function getAmaronChildDispositionColumnIds(level: AmaronDispositionLevelKey): Array<Uuid> {
    return getAmaronChildDispositionLevelKeys(level).map((childLevel) => {
        switch (childLevel) {
            case "l1":
                return AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID;
            case "l2":
                return AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID;
            case "l3":
                return AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID;
            case "l4":
                return AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID;
        }
    });
}

const EMPTY_AMARON_DISPOSITION_LEVELS: AmaronDispositionLevels = {l1: "", l2: "", l3: "", l4: ""};

export function buildAmaronJourneySyntheticColumnValues(source: {disposition: Uuid | null | undefined}): Record<Uuid, string> {
    if (source.disposition === null || source.disposition === undefined) {
        return {
            [AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID]: "",
            [AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID]: "",
            [AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID]: "",
            [AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID]: "",
        };
    }
    const flat = amaronFlatDispositionForDispositionId(source.disposition);
    const levels = flat === undefined ? EMPTY_AMARON_DISPOSITION_LEVELS : splitAmaronFlatDispositionIntoLevels(flat);
    return {
        [AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID]: levels.l1,
        [AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID]: levels.l2,
        [AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID]: levels.l3,
        [AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID]: levels.l4,
    };
}

export function buildAmaronCallsEventSyntheticDispositionColumnValues(dispositionId: Uuid | null | undefined): Record<Uuid, string> {
    if (dispositionId === null || dispositionId === undefined) {
        return {
            [AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID]: "",
            [AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID]: "",
            [AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID]: "",
            [AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID]: "",
        };
    }
    const flat = amaronFlatDispositionForDispositionId(dispositionId);
    const levels = flat === undefined ? EMPTY_AMARON_DISPOSITION_LEVELS : splitAmaronFlatDispositionIntoLevels(flat);
    return {
        [AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID]: levels.l1,
        [AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID]: levels.l2,
        [AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID]: levels.l3,
        [AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID]: levels.l4,
    };
}

/** Merges synthetic L1–L4 disposition strings into journey `additionalColumnValues`. */
export function amaronJourneySyntheticFunction(
    journey: EventContainerWithEvents,
    journeyDispositionId: Uuid | null | undefined,
): EventContainerWithEvents {
    const synthetic = buildAmaronJourneySyntheticColumnValues({disposition: journeyDispositionId});
    return {
        ...journey,
        additionalColumnValues: {
            ...journey.additionalColumnValues,
            ...synthetic,
        },
    };
}

export function buildAmaronCallSyntheticColumnValues(
    event: EventWithEventContainer,
    eventTemplate: OttopilotEventTemplate,
): Record<Uuid, string> {
    if ((event.templateId as string) !== String(AmaronEventTypes.Calls)) {
        return {};
    }
    const storedDispositionId =
        (event.additionalColumnValues[AMARON__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID] as Uuid | undefined)
        ?? getEventDispositionId(event, eventTemplate);
    return buildAmaronCallsEventSyntheticDispositionColumnValues(storedDispositionId);
}

export function amaronCallEventSyntheticFunction(
    event: EventWithEventContainer,
    eventTemplate: OttopilotEventTemplate,
): EventWithEventContainer {
    const synthetic = buildAmaronCallSyntheticColumnValues(event, eventTemplate);
    const resolvedScheduledOn = resolveAmaronCallScheduledOnDisplayValue(event.additionalColumnValues);
    return {
        ...event,
        additionalColumnValues: {
            ...event.additionalColumnValues,
            ...synthetic,
            ...(resolvedScheduledOn !== undefined
                ? {[AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID]: resolvedScheduledOn}
                : {}),
        },
    };
}

export function getAmaronDayPlanTableViewColumns(): Array<JourneyTableViewColumn> {
    const journeyColumn = (
        id: Uuid,
        displayName: string,
        type: UdoColumnType = UdoColumnType.string,
    ): JourneyTableViewColumn => ({
        id,
        field: id,
        isAdditionalColumn: true,
        columnType: "OttopilotEventContainer",
        type,
        displayName,
        sortingCapability: true,
        filteringCapability_contains: true,
    });

    const systemColumn = (id: Uuid, field: "disposition", displayName: string): JourneyTableViewColumn => ({
        id,
        field,
        isAdditionalColumn: false,
        columnType: "OttopilotEventContainer",
        displayName,
        sortingCapability: true,
        filteringCapability_contains: true,
    });

    return [
        journeyColumn(AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID, "Customer Name"),
        journeyColumn(AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID, "Priority"),
        journeyColumn(
            AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
            "Contact Number",
            UdoColumnType.indianPhoneNumber,
        ),
        journeyColumn(AMARON__EVENT_CONTAINER__CUSTOMER_HOUSE_NO__COLUMN_ID, "Customer House No."),
        journeyColumn(AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID, "City"),
        journeyColumn(AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID, "State"),
        journeyColumn(
            AMARON__EVENT_CONTAINER__PREFERRED_CALLING_TIME__COLUMN_ID,
            "Preferred Calling Time",
            UdoColumnType.userDefinedObject,
        ),
        journeyColumn(AMARON__EVENT_CONTAINER__LEAD_CATEGORY__COLUMN_ID, "Lead Category", UdoColumnType.userDefinedObject),
        journeyColumn(AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID, "Assigned Agent", UdoColumnType.user),
        systemColumn("899b476b-3b75-47ea-b47d-b98fe339c448" as Uuid, "disposition", "Disposition"),
        journeyColumn(AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID, "Last Activity", UdoColumnType.userDefinedObject),
        journeyColumn(
            AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
            "Last Activity Timestamp",
            UdoColumnType.timestamp,
        ),
        journeyColumn(AMARON__EVENT_CONTAINER__CAR_MAKE__COLUMN_ID, "Car Make"),
        journeyColumn(AMARON__EVENT_CONTAINER__CAR_MODEL__COLUMN_ID, "Car Model"),
        journeyColumn(AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID, "Fuel Type", UdoColumnType.userDefinedObject),
        journeyColumn(AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID, "Service Requested", UdoColumnType.userDefinedObject),
        journeyColumn(AMARON__EVENT_CONTAINER__SMARTPING_CAMPAIGN__COLUMN_ID, "Smartping Campaign"),
        journeyColumn(
            AMARON__EVENT_CONTAINER__SERVICE_UPGRADE_REQUIRED__COLUMN_ID,
            "Service Upgrade Required",
            UdoColumnType.userDefinedObject,
        ),
        journeyColumn(AMARON__EVENT_CONTAINER__LINK_NAME__COLUMN_ID, "Booking Status"),
        journeyColumn(AMARON__EVENT_CONTAINER__QUOTED_PRICE__COLUMN_ID, "Quoted price", UdoColumnType.number),
        journeyColumn(AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID, "Utm_platform"),
        journeyColumn(AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID, "Utm_source"),
        journeyColumn(AMARON__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID, "Utm_medium"),
        journeyColumn(AMARON__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID, "Utm_campaign"),
        journeyColumn(AMARON__EVENT_CONTAINER__UTM_TERM__COLUMN_ID, "Utm_term"),
        journeyColumn(AMARON__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID, "Utm_content"),
    ];
}

/** Default filter-bar chips for Amaron list modules (Leads, In Queue, Events, Day Plan, Disposition Tracker). */
export const AMARON_DEFAULT_VISIBLE_FILTER_COLUMN_IDS: ReadonlyArray<string> = [
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
];

/** @deprecated Use {@link AMARON_DEFAULT_VISIBLE_FILTER_COLUMN_IDS} */
export const AMARON_DAY_PLAN_DEFAULT_VISIBLE_FILTER_COLUMN_IDS: Array<string> = [...AMARON_DEFAULT_VISIBLE_FILTER_COLUMN_IDS];

export function mergeUniqueOrderedColumnIds(
    ...columnIdGroups: ReadonlyArray<ReadonlyArray<string>>
): Array<string> {
    const seen = new Set<string>();
    const merged: Array<string> = [];
    for (const group of columnIdGroups) {
        for (const columnId of group) {
            const key = String(columnId);
            if (seen.has(key) === true) {
                continue;
            }
            seen.add(key);
            merged.push(key);
        }
    }
    return merged;
}

/** Ensures default filter chips have backing Ottopilot columns (table + filter bar stay in sync). */
export function mergeAmaronJourneyDefaultFilterColumnIdsIntoVisibleColumns(
    visibleColumnIds: ReadonlyArray<string>,
): Array<string> {
    return mergeUniqueOrderedColumnIds(visibleColumnIds, AMARON_DEFAULT_VISIBLE_FILTER_COLUMN_IDS);
}

/** Event templates use event-scoped column ids for some fields (e.g. UTM source on Calls). */
export function getAmaronDefaultVisibleFilterColumnIdsForEventTemplate(eventTemplateId: Uuid): Array<string> {
    const sharedJourneyFilterColumnIds = [
        AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
        AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
        AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    ] as const;

    switch (String(eventTemplateId)) {
        case String(AmaronEventTypes.Enquiry):
            return [
                ...sharedJourneyFilterColumnIds,
                AMARON__EVENT__ENQUIRY__SERVICE_REQUESTED__COLUMN_ID,
                AMARON__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
            ];
        case String(AmaronEventTypes.Calls):
            return [
                ...sharedJourneyFilterColumnIds,
                AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
                AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
            ];
        case String(AmaronEventTypes.Service):
            return [
                ...sharedJourneyFilterColumnIds,
                AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
                AMARON__EVENT__SERVICE__UTM_SOURCE__COLUMN_ID,
            ];
        default:
            return [...AMARON_DEFAULT_VISIBLE_FILTER_COLUMN_IDS];
    }
}

/** @deprecated Prefer {@link mergeAmaronJourneyDefaultFilterColumnIdsIntoVisibleColumns} for journey modules. */
export function resolveAmaronDefaultVisibleFilterColumnIds(allowedColumnIds: ReadonlyArray<string>): Array<string> {
    const allowed = new Set(allowedColumnIds.map(String));
    return AMARON_DEFAULT_VISIBLE_FILTER_COLUMN_IDS.filter((columnId) => allowed.has(String(columnId)));
}

const AMARON_DAY_PLAN_KANBAN_COLUMN_HEADER_STYLE = {
    headerBorderRadius: "4px 4px 0 0",
    headerBorderColor: "var(--ds-border)",
    headerBorderWidth: "0.5px",
} as const;

/** Wider columns so disposition-tracker-style cards fit without horizontal scroll inside columns. */
export const AMARON_DAY_PLAN_KANBAN_LAYOUT = {
    columnMinWidthPx: 400,
    columnGapPx: 16,
    mobileCardsGridTemplateColumns: "1fr",
} as const;

/** InQueue kanban: hide column pin; stack cards full-width on mobile instead of 3-across. */
export const AMARON_INQUEUE_KANBAN_LAYOUT = {
    hideColumnPin: true,
    mobileCardsGridTemplateColumns: "1fr",
} as const;

export function getAmaronDayPlanCategoryKanbanColumnConfig(): Array<KanbanBoardColumnConfig> {
    return [
        {
            id: AmaronLeadCategoryEnum.EMERGENCY as Uuid,
            displayName: amaronLeadCategories[AmaronLeadCategoryEnum.EMERGENCY].displayName,
            background: token("color.background.accent.gray.subtler"),
            textColor: token("color.text"),
            ...AMARON_DAY_PLAN_KANBAN_COLUMN_HEADER_STYLE,
            possibleValues: [AmaronLeadCategoryEnum.EMERGENCY],
        },
        {
            id: AmaronLeadCategoryEnum.AT_RISK as Uuid,
            displayName: amaronLeadCategories[AmaronLeadCategoryEnum.AT_RISK].displayName,
            background: token("color.background.accent.gray.subtler"),
            textColor: token("color.text"),
            ...AMARON_DAY_PLAN_KANBAN_COLUMN_HEADER_STYLE,
            possibleValues: [AmaronLeadCategoryEnum.AT_RISK],
        },
        {
            id: AmaronLeadCategoryEnum.HOT as Uuid,
            displayName: amaronLeadCategories[AmaronLeadCategoryEnum.HOT].displayName,
            background: token("color.background.accent.gray.subtler"),
            textColor: token("color.text"),
            ...AMARON_DAY_PLAN_KANBAN_COLUMN_HEADER_STYLE,
            possibleValues: [AmaronLeadCategoryEnum.HOT],
        },
        {
            id: AmaronLeadCategoryEnum.WARM as Uuid,
            displayName: amaronLeadCategories[AmaronLeadCategoryEnum.WARM].displayName,
            background: token("color.background.accent.gray.subtler"),
            textColor: token("color.text"),
            ...AMARON_DAY_PLAN_KANBAN_COLUMN_HEADER_STYLE,
            possibleValues: [AmaronLeadCategoryEnum.WARM],
        },
        {
            id: AmaronLeadCategoryEnum.COLD as Uuid,
            displayName: amaronLeadCategories[AmaronLeadCategoryEnum.COLD].displayName,
            background: token("color.background.accent.gray.subtler"),
            textColor: token("color.text"),
            ...AMARON_DAY_PLAN_KANBAN_COLUMN_HEADER_STYLE,
            possibleValues: [AmaronLeadCategoryEnum.COLD],
        },
    ];
}

export function getAmaronDayPlanPageConfigFallback(): DayPlanPageConfig {
    return {
        navLabel: "My Day Plan",
        routePath: "my-day-plan",
        visibleToUserTemplateIds: [AmaronRoles.Agent as Uuid],
        moduleDisplayName: {
            singularSentenceCase: "Lead",
            pluralSentenceCase: "Leads",
            singularLowerCase: "lead",
            pluralLowerCase: "leads",
        },
        ottopilotDataViewerConfig: {
            searchColumns: [AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID, AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID],
            searchPlaceholder: "Search my day plan",
        },
        itemSource: {
            kind: "eventContainersWithEvents",
            assignmentColumnId: AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
            fixedFilters: [
                {
                    columnId: AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
                    operation: FilterOperation.equals,
                    value: amaronLeadStatuses[AmaronLeadStatusEnum.LIVE].id,
                },
            ],
        },
        tableViewColumns: getAmaronDayPlanTableViewColumns(),
        kanbanBoardPageView: {
            filterFieldId: AMARON__EVENT_CONTAINER__LEAD_CATEGORY__COLUMN_ID,
            columnConfig: getAmaronDayPlanCategoryKanbanColumnConfig(),
        },
        listView: {
            enabled: true,
            showFooter: true,
        },
        focusView: {
            enabled: true,
        },
        yourQueue: {
            categoryColumnId: AMARON__EVENT_CONTAINER__LEAD_CATEGORY__COLUMN_ID,
        },
    };
}

export type AmaronStatsCardConfig = {
    dispositionIds: Array<Uuid>;
    label: string;
    infoText?: string;
};

export type AmaronLeadHealthConfig = Array<{
    label: string;
    children: Array<{
        dispositionIds: Array<Uuid>;
        label: string;
    }>;
}>;

export const AMARON_STATS_CARD_CONFIG: Array<AmaronStatsCardConfig> = [
    {
        label: "Follow-Ups Due",
        dispositionIds: getAmaronDispositionIdsForLevels({
            l4Values: ["follow-up"],
        }),
    },
    {
        label: "New Leads",
        dispositionIds: getAmaronDispositionIdsForLevels({
            l1Values: ["new-lead"],
        }),
        infoText: "Leads update in real time — everything assigned to you today appears here automatically.",
    },
    {
        label: "Call Back Later",
        dispositionIds: getAmaronDispositionIdsForLevels({
            l2Values: ["call-back-later"],
        }),
        infoText: "Shows all calls scheduled for today. Updates automatically as new callbacks are added.",
    },
    {
        label: "Bookings",
        dispositionIds: getAmaronDispositionIdsForLevels({
            l4Values: ["booked"],
        }),
    },
];

export const AMARON_LEAD_HEALTH_CONFIG: AmaronLeadHealthConfig = [
    {
        label: "Reachability",
        children: [
            {
                label: "Not Connected",
                dispositionIds: getAmaronDispositionIdsForLevels({
                    l2Values: ["nr1", "nr2", "nr3", "nr4", "nr5", "nr6"],
                }),
            },
            {
                label: "Follow Up",
                dispositionIds: getAmaronDispositionIdsForLevels({
                    l4Values: ["follow-up"],
                }),
            },
        ],
    },
    {
        label: "Intent",
        children: [
            {
                label: "Not Interested",
                dispositionIds: getAmaronDispositionIdsForLevels({
                    l3Values: ["not-interested"],
                }),
            },
            {
                label: "Interested",
                dispositionIds: getAmaronDispositionIdsForLevels({
                    l3Values: ["interested"],
                }),
            },
        ],
    },
    {
        label: "Conversion",
        children: [
            {
                label: "Booked",
                dispositionIds: getAmaronDispositionIdsForLevels({
                    l4Values: ["booked"],
                }),
            },
        ],
    },
];

// Call form Ids

/** Earliest selectable instant for Amaron call schedule pickers (allows backdating; overrides implicit “today” min). */
export const AMARON_CALL_SCHEDULE_PICKER_MIN_DATE = new Date("1970-01-01T00:00:00.000Z");

// Service form Ids

const amaronCallsScheduledVisibilityRule: OttopilotAdditionalButtonVisibilityRule = {
    conditions: [
        {
            attribute: AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
            operator: OttopilotOperator.equals,
            kind: OttopilotRunTimeValueKind.static,
            value: AmaronCallStatusEnum.SCHEDULED as string,
        },
    ],
    logicalOperator: "and",
};

export function getAmaronCallStatusId(event: {additionalColumnValues: Record<Uuid, unknown>}): string | null {
    const raw = event.additionalColumnValues[AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID];
    if (typeof raw !== "string") {
        return null;
    }
    const trimmed = raw.trim();
    return trimmed === "" ? null : trimmed;
}

export function isAmaronScheduledCallEvent(event: {additionalColumnValues: Record<Uuid, unknown>}): boolean {
    return getAmaronCallStatusId(event) === String(AmaronCallStatusEnum.SCHEDULED);
}

export function isAmaronCancelledCallEvent(event: {additionalColumnValues: Record<Uuid, unknown>}): boolean {
    return getAmaronCallStatusId(event) === String(AmaronCallStatusEnum.CANCELLED);
}

export function isAmaronLoggedCallEvent(event: {additionalColumnValues: Record<Uuid, unknown>}): boolean {
    return getAmaronCallStatusId(event) === String(AmaronCallStatusEnum.LOGGED);
}

/** Hide shared "Log a call" for statuses that must not open log again. */
export function shouldHideAmaronCallLogAction(event: {additionalColumnValues: Record<Uuid, unknown>}): boolean {
    return isAmaronCancelledCallEvent(event) === true || isAmaronLoggedCallEvent(event) === true;
}

/**
 * Cancel for scheduled calls (reason required).
 * Log is handled by the shared row "Log a call" action (scheduled → eventId; logged/cancelled → hidden).
 */
export const AMARON__CALLS_EVENT__ADDITIONAL_BUTTONS: Array<OttopilotAdditionalButtonsConfig> = [
    {
        id: "57f77702-0513-4644-8218-7997edfc27fc" as Uuid,
        label: "Cancel call",
        buttonKind: OttopilotAdditionalButtonKind.cancel,
        description: "Cancels a scheduled call. Cancellation reason is required (system auto-cancel may use a fixed remark).",
        rules: [amaronCallsScheduledVisibilityRule],
        showModal: true,
        formColumnData: [
            {
                columnId: AMARON__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.user,
                sortOrder: 0,
            },
            {
                columnId: AMARON__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.timestamp,
                sortOrder: 1,
            },
            {
                columnId: AMARON__EVENT__CALLS__CANCELLATION_REASON__COLUMN_ID,
                columnType: "OttopilotEvent",
                fieldRole: OttopilotAdditionalButtonFormFieldRole.cancelRemarks,
                isVisible: true,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                presentation: OttopilotAdditionalButtonFormFieldPresentation.multiLineText,
                sortOrder: 2,
            },
            {
                columnId: AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                defaultValue: AmaronCallStatusEnum.CANCELLED as string,
                sortOrder: 3,
            },
        ],
    },
    {
        id: "98763508-4148-4435-ab4c-2f3e13d22609" as Uuid,
        label: "Log",
        buttonKind: OttopilotAdditionalButtonKind.log,
        rules: [amaronCallsScheduledVisibilityRule],
        formUrl: "amaron/call/log",
        formUrlQueryParams: ["eventContainerId", "eventId"],
    },
];

/** Amaron service event actions are disabled in the latest hotfix flow. */
export const AMARON__SERVICE_EVENT__ADDITIONAL_BUTTONS: Array<OttopilotAdditionalButtonsConfig> = [];

export const AMARON_DEFAULT_JOURNEY_TRIGGER_CONFIG: OttopilotTriggerConfig = [
    {
        type: OttopilotTrigger.dateTimeTrigger,
        eventTemplateId: AmaronEventTypes.Calls as Uuid,
        triggerDispositions: [
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_FOLLOW_UP].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NOT_CONNECTED_NR1].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NOT_CONNECTED_NR2].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NOT_CONNECTED_NR3].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NOT_CONNECTED_NR4].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NOT_CONNECTED_NR5].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NOT_CONNECTED_CALL_HUNG_UP].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_OTHER].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_SLOT_NOT_AVAILABLE].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_NEED_MORE_INFORMATION].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_VALID_SHORT_HANG_UP].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_VALID_TRANSFERRED_TO_VOICEMAIL].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER_CBL1].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER_CBL2].id,
            amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER_CBL3].id,
        ],
        dataColumns: [
            {
                columnId: AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: AmaronCallStatusEnum.SCHEDULED,
            },
            {
                columnId: AMARON__EVENT__CALLS__CREATED_BY_ROLE__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.workspaceUserTemplateId,
            },
            {
                columnId: AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
                columnType: "OttopilotEventContainer",
                driver: OttopilotTriggerDataDriver.workspaceUser,
            },
            {
                columnId: AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: null,
            },
            {
                columnId: AMARON__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.workspaceUser,
            },
            {
                columnId: AMARON__EVENT_CONTAINER__LAST_EVENT__COLUMN_ID,
                columnType: "OttopilotEventContainer",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: AmaronEventTypes.Calls as Uuid,
            },
            {
                columnId: AMARON__EVENT_CONTAINER__LAST_EVENT_TIMESTAMP__COLUMN_ID,
                columnType: "OttopilotEventContainer",
                driver: OttopilotTriggerDataDriver.currentTimestamp,
            },
        ],
        dateTimeColumnId: AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
        scheduleFieldLabel: "Schedule call",
        scheduleValueEncoding: OttopilotScheduleValueEncoding.duluxIst,
    },
];

export const amaronDispositionConfig: OttopilotDispositionConfig = {
    emitWhen: OttopilotDispositionEmitWhen.pathMatchesKnownDisposition,
    plugins: [
        {
            kind: OttopilotDispositionPluginKind.dispositionHistory,
            lastN: 20,
        },
        {
            kind: OttopilotDispositionPluginKind.optionRules,
            rules: [
                {
                    id: "f3a8b2c1-9d4e-4789-b012-567890abcdef" as Uuid,
                    when: {
                        level: 0,
                        selectedPrefix: [],
                    },
                    targetSegment: "new-lead",
                    countDispositionId: amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NEW_LEAD].id,
                    countMode: OttopilotDispositionOptionRuleCountMode.totalInHistory,
                    action: {
                        kind: OttopilotDispositionOptionRuleActionKind.hideWhenCurrentDispositionIdEquals,
                    },
                },
                {
                    id: "8c017654-8eb6-4b28-839a-90ab43b45526" as Uuid,
                    when: {
                        level: 0,
                        selectedPrefix: [],
                    },
                    targetSegment: "new-lead",
                    countDispositionId: amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NEW_LEAD].id,
                    countMode: OttopilotDispositionOptionRuleCountMode.totalInHistory,
                    action: {
                        kind: OttopilotDispositionOptionRuleActionKind.hideWhenHistoryContainsNonAllowedDispositionId,
                        allowedDispositionIds: [amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NEW_LEAD].id],
                    },
                },
            ],
        },
    ],
};

export function isAmaronKanbanListEventTemplate(eventTemplateId: Uuid): boolean {
    return (
        eventTemplateId === (AmaronEventTypes.Enquiry as Uuid) ||
        eventTemplateId === (AmaronEventTypes.Calls as Uuid) ||
        eventTemplateId === (AmaronEventTypes.Service as Uuid)
    );
}

/** Status column used when archiving Amaron enquiry / call / service events. */
export function resolveAmaronEventTemplateStatusColumnConfig(
    eventTemplateId: Uuid,
): NonNullable<OttopilotEventTemplate["configuration"]["statusColumnConfig"]> | undefined {
    switch (String(eventTemplateId)) {
        case String(AmaronEventTypes.Enquiry):
            return {
                columnId: AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
                archiveStatusValue: AmaronEnquiryStatusEnum.ARCHIVE as Uuid,
            };
        case String(AmaronEventTypes.Calls):
            return {
                columnId: AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
                archiveStatusValue: AmaronCallStatusEnum.ARCHIVE as Uuid,
            };
        case String(AmaronEventTypes.Service):
            return {
                columnId: AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID,
                archiveStatusValue: AmaronServiceCategoryEnum.ARCHIVE as Uuid,
            };
        default:
            return undefined;
    }
}

/** Ensures Amaron event templates expose archive config for the events module UI. */
export function withAmaronEventArchiveStatusColumnConfig(
    workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>,
    eventTemplate: OttopilotEventTemplate,
): OttopilotEventTemplate {
    if (isAmaronWorkspace(workspaceCapabilities) !== true || eventTemplate.configuration.statusColumnConfig !== undefined) {
        return eventTemplate;
    }
    const statusColumnConfig = resolveAmaronEventTemplateStatusColumnConfig(eventTemplate.id);
    if (statusColumnConfig === undefined) {
        return eventTemplate;
    }
    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            statusColumnConfig,
        },
    };
}
